// TestimonialsSection.jsx
// (Optional fonts for best match – include in index.html)
// <link rel="preconnect" href="https://fonts.googleapis.com" />
// <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
// <link href="https://fonts.googleapis.com/css2?family=Archivo:wght@700;800&family=DM+Sans:wght@400;500&display=swap" rel="stylesheet" />

import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Container,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";

const testimonialsData = [
  {
    name: "Sarah J., Bloomington",
    role: "Homeowner",
    title: "The Emergency Savior",
    avatar:
      "https://html.themehour.net/kleanix/demo/assets/img/testimonial/testi_1_6.jpg",
    text: "We woke up to a flooded basement after that last heavy storm. I called Rite Home Pros at 3 AM, and they were here within the hour. They dried everything out before mold could set in. Knowing they also handle our HVAC maintenance now gives me huge peace of mind.",
  },
  {
    name: "Mark & Emily T., Ellettsville ",
    role: "Homeowner",
    title: "The Convenience Factor",
    avatar:
      "https://html.themehour.net/kleanix/demo/assets/img/testimonial/testi_1_7.jpg",
    text: "I used to always forget to change my furnace filters until the system overheated. The Rite Home Pros membership is a no-brainer. They come out, tune up the AC and Furnace, and even bring the filters to me. For $125 a month, it pays for itself.",
  },
  {
    name: "David R., Near IU Campus ",
    role: "Homeowner",
    title: "Top Tier Cleaning",
    avatar:
      "https://html.themehour.net/kleanix/demo/assets/img/testimonial/testi_1_8.jpg",
    text: "It’s hard to find a cleaning service that does a deep clean and understands home systems. The team was professional, thorough, and respectful of my home. My house hasn't smelled this fresh in years!",
  }, {
    name: "Jessica M., Martinsville",
    role: "Homeowner",
    title: "Spotless & Stress-Free",
    avatar:
      "https://html.themehour.net/kleanix/demo/assets/img/testimonial/testi_1_1.jpg",
    text: "With two kids and a dog, keeping the house clean felt impossible. Rite Home Pros made it effortless. Their cleaning team didn’t miss a single corner, and everything felt fresh and organized when they were done.",
  },
  {
    name: "Anthony K., Bloomington",
    role: "Homeowner",
    title: "Reliable HVAC Experts",
    avatar:
      "https://html.themehour.net/kleanix/demo/assets/img/testimonial/testi_1_2.jpg",
    text: "Our AC stopped working right in the middle of summer. The technician arrived on time, explained the issue clearly, and had it fixed the same day. Honest pricing and no pressure—exactly what you want in an HVAC company.",
  },
  {
    name: "Linda P., Ellettsville",
    role: "Homeowner",
    title: "Handled the Worst with Care",
    avatar:
      "https://html.themehour.net/kleanix/demo/assets/img/testimonial/testi_1_3.jpg",
    text: "After a pipe burst in our laundry room, I was overwhelmed. Rite Home Pros handled the water mitigation from start to finish. They communicated every step and left our home completely restored. I couldn’t have asked for better support.",
  }
];

const TestimonialsSection = () => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md")); // ≥ 900px
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg")); // ≥ 1200px

  const perView = isLgUp ? 3 : isMdUp ? 2 : 1;

  const [slideIndex, setSlideIndex] = useState(0);

  const totalSlides = useMemo(
    () => Math.ceil(testimonialsData.length / perView),
    [perView]
  );

  useEffect(() => {
    const id = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % totalSlides);
    }, 5000); // 5 seconds
    return () => clearInterval(id);
  }, [totalSlides]);

  const visibleTestimonials = useMemo(() => {
    const items = [];
    for (let i = 0; i < perView; i++) {
      const idx = (slideIndex * perView + i) % testimonialsData.length;
      items.push(testimonialsData[idx]);
    }
    return items;
  }, [slideIndex, perView]);

  return (
    <>
      <style>{`
        :root {
          --testi-accent: #7ac142;
          --testi-heading: #111827;
          --testi-body: #6b7280;
          --testi-card-bg: #ffffff;
          --testi-card-shadow: 0 26px 80px rgba(15, 23, 42, 0.06);
          --testi-card-radius: 32px;
          --testi-grid-img: url("https://html.themehour.net/kleanix/demo/assets/img/shape/testimonials-bg-3.png");
          --testi-heading-font: "Archivo", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          --testi-body-font: "DM Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          --testi-ease: cubic-bezier(0.16, 1, 0.3, 1);
        }

        .testi-section {
          position: relative;
          padding: 96px 16px 90px;
          background-color: #ffffff;
          overflow: hidden;
        }

        .testi-inner {
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
        }

        .testi-shape {
          position: absolute;
          top: 40px;
          right: -80px;
          width: 520px;
          opacity: 0.5;
          pointer-events: none;
          z-index: 0;
          background-image: var(--testi-grid-img);
          background-repeat: no-repeat;
          background-size: contain;
          background-position: top right;
        }

        /* heading */
        .testi-heading-wrap {
          margin-bottom: 40px;
        }

        .testi-kicker-wrap {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
        }

        .testi-kicker-line {
          width: 44px;
          height: 2px;
          border-radius: 999px;
          background: var(--testi-accent);
        }

        .testi-kicker {
          font-family: var(--testi-body-font);
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--testi-accent);
        }

        .testi-title {
          font-family: var(--testi-heading-font);
          font-weight: 800;
          font-size: 34px;
          line-height: 1.22;
          color: var(--testi-heading);
          margin: 0;
        }

        /* slider row - HORIZONTAL */
        .testi-slider-row {
          position: relative;
          z-index: 1;
          display: flex;
          gap: 24px;
          align-items: stretch;
          animation: testi-fade 600ms var(--testi-ease) both;
        }

        @keyframes testi-fade {
          0% {
            opacity: 0;
            transform: translate3d(0, 18px, 0);
          }
          100% {
            opacity: 1;
            transform: translate3d(0, 0, 0);
          }
        }

        .testi-card-wrapper {
          flex: 1 1 0;
          min-width: 0;
        }

        .testi-card {
          background: var(--testi-card-bg);
          border-radius: var(--testi-card-radius);
          box-shadow: var(--testi-card-shadow);
          padding: 26px 30px 28px;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .testi-top {
          display: flex;
          align-items: center;
          margin-bottom: 18px;
        }

        .testi-avatar {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          overflow: hidden;
          margin-right: 16px;
          flex-shrink: 0;
        }

        .testi-name {
          font-family: var(--testi-heading-font);
          font-size: 16px;
          font-weight: 700;
          color: var(--testi-heading);
          margin-bottom: 2px;
        }

        .testi-role {
          font-family: var(--testi-body-font);
          font-size: 13px;
          color: var(--testi-body);
        }

        .testi-stars {
          display: flex;
          gap: 4px;
          margin-bottom: 8px;
        }

        .testi-label {
          font-family: var(--testi-heading-font);
          font-size: 16px;
          font-weight: 700;
          color: var(--testi-heading);
          margin-bottom: 8px;
        }

        .testi-text {
          font-family: var(--testi-body-font);
          font-size: 14px;
          line-height: 1.8;
          color: var(--testi-body);
        }

        /* responsive */
        @media (max-width: 1199px) {
          .testi-section {
            padding-top: 80px;
            padding-bottom: 80px;
          }
          .testi-title {
            font-size: 30px;
          }
          .testi-shape {
            width: 420px;
            top: 120px;
          }
        }

        @media (max-width: 959px) {
          .testi-title {
            font-size: 28px;
          }
          .testi-slider-row {
            flex-wrap: wrap;
          }
          .testi-card-wrapper {
            flex: 1 1 calc(50% - 12px);
          }
          .testi-shape {
            opacity: 0.35;
          }
        }

        @media (max-width: 599px) {
          .testi-section {
            padding-top: 64px;
            padding-bottom: 64px;
          }
          .testi-title {
            font-size: 24px;
          }
          .testi-card {
            padding: 22px 22px 24px;
          }
          .testi-slider-row {
            flex-direction: column;
          }
          .testi-card-wrapper {
            flex: 1 1 100%;
          }
          .testi-shape {
            display: none;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .testi-slider-row {
            animation: none !important;
          }
        }
      `}</style>

      <Box component="section" className="testi-section" id="testi-sec">
        <Container className="testi-inner">
          <Box className="testi-shape" aria-hidden="true" />

          {/* Heading */}
          <Box className="testi-heading-wrap">
            <Box className="testi-kicker-wrap">
              <Box className="testi-kicker-line" />
              <Typography className="testi-kicker">TESTIMONIALS</Typography>
              <Box className="testi-kicker-line" />
            </Box>
            <Typography component="h2" className="testi-title">
              Neighbours in Bloomington  
              <br />
Love the Rite Way
            </Typography>
          </Box>

          {/* Horizontal auto‑carousel */}
          <Box className="testi-slider-row" key={slideIndex}>
            {visibleTestimonials.map((item, idx) => (
              <Box
                className="testi-card-wrapper"
                key={`${item.name}-${idx}`}
              >
                <Box className="testi-card">
                  <Box className="testi-top">
                    <Avatar
                      src={item.avatar}
                      alt={item.name}
                      className="testi-avatar"
                    />
                    <Box>
                      <Typography className="testi-name">
                        {item.name}
                      </Typography>
                      <Typography className="testi-role">
                        {item.role}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    className="testi-stars"
                    aria-label="5 star review"
                    role="img"
                  >
                    {Array.from({ length: 5 }).map((_, i) => (
                      <StarIcon
                        key={i}
                        sx={{ fontSize: 18, color: "#ffc93b" }}
                      />
                    ))}
                  </Box>

                  <Typography className="testi-label">{item.title}</Typography>
                  <Typography className="testi-text">{item.text}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default TestimonialsSection;
